import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { apiGet, apiPost } from '../api/client';

const PenIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

function SetupPage({ onDone, isUpdate }) {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [cuzkUsername, setCuzkUsername] = useState('');
  const [cuzkPassword, setCuzkPassword] = useState('');
  const [cuzkApiKey, setCuzkApiKey] = useState('');
  const [templateFile, setTemplateFile] = useState(null);
  const [saleFlatTemplateFile, setSaleFlatTemplateFile] = useState(null);
  const [reservationTemplateFile, setReservationTemplateFile] = useState(null);
  const [gdprTemplateFile, setGdprTemplateFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [error, setError] = useState(null);
  const [existingTemplates, setExistingTemplates] = useState({
    templateUploaded: false,
    saleFlatTemplateUploaded: false,
    reservationTemplateUploaded: false,
    gdprTemplateUploaded: false,
  });

  useEffect(() => {
    if (!isUpdate) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet('/api/agent-config', getToken);
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && data.hasConfig) {
          if (data.cuzk_username != null) setCuzkUsername(String(data.cuzk_username));
          if (data.cuzk_api_key != null) setCuzkApiKey(String(data.cuzk_api_key));
          setExistingTemplates({
            templateUploaded: !!data.templateUploaded,
            saleFlatTemplateUploaded: !!data.saleFlatTemplateUploaded,
            reservationTemplateUploaded: !!data.reservationTemplateUploaded,
            gdprTemplateUploaded: !!data.gdprTemplateUploaded,
          });
        }
      } catch (e) {
        if (!cancelled) setLoadError(e.message || 'Failed to load profile.');
      }
    })();
    return () => { cancelled = true; };
  }, [isUpdate, getToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      let template_base64 = null;
      let template_filename = null;
      if (templateFile && templateFile.size > 0) {
        template_base64 = await fileToBase64(templateFile);
        template_filename = templateFile.name || 'RENT_1_template.docx';
      }

      let sale_flat_template_base64 = null;
      let sale_flat_template_filename = null;
      if (saleFlatTemplateFile && saleFlatTemplateFile.size > 0) {
        sale_flat_template_base64 = await fileToBase64(saleFlatTemplateFile);
        sale_flat_template_filename = saleFlatTemplateFile.name || 'SALE_Order_Flat_template.docx';
      }

      let reservation_template_base64 = null;
      let reservation_template_filename = null;
      if (reservationTemplateFile && reservationTemplateFile.size > 0) {
        reservation_template_base64 = await fileToBase64(reservationTemplateFile);
        reservation_template_filename = reservationTemplateFile.name || 'rezervacni_smlouva_template.docx';
      }

      let gdpr_template_base64 = null;
      if (gdprTemplateFile && gdprTemplateFile.size > 0) {
        gdpr_template_base64 = await fileToBase64(gdprTemplateFile);
      }

      const templateOnly =
        isUpdate
        && (template_base64 || sale_flat_template_base64 || reservation_template_base64 || gdpr_template_base64)
        && !cuzkUsername.trim()
        && !cuzkApiKey.trim();
      let res;
      if (templateOnly) {
        const payload = {};
        if (template_base64) {
          payload.template_base64 = template_base64;
          payload.template_filename = template_filename;
        }
        if (sale_flat_template_base64) {
          payload.sale_flat_template_base64 = sale_flat_template_base64;
          payload.sale_flat_template_filename = sale_flat_template_filename;
        }
        if (reservation_template_base64) {
          payload.reservation_template_base64 = reservation_template_base64;
          payload.reservation_template_filename = reservation_template_filename;
        }
        if (gdpr_template_base64) payload.gdpr_template_base64 = gdpr_template_base64;
        res = await apiPost('/api/agent-config', payload, getToken);
      } else {
        const payload = {
          cuzk_username: cuzkUsername.trim(),
          cuzk_api_key: cuzkApiKey.trim(),
          template_base64,
          template_filename,
          sale_flat_template_base64,
          sale_flat_template_filename,
          reservation_template_base64,
          reservation_template_filename,
          gdpr_template_base64,
        };
        if (!isUpdate || (cuzkPassword && cuzkPassword.trim() !== '')) {
          payload.cuzk_password = cuzkPassword || '';
        }
        res = await apiPost('/api/agent-config', payload, getToken);
      }

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data.detail ? `${data.error || 'Error'} — ${data.detail}` : (data.error || 'Failed to save config.');
        throw new Error(msg);
      }
      if (onDone) onDone();
    } catch (err) {
      setError(err.message || 'Failed to save.');
    } finally {
      setLoading(false);
    }
  };

  const hasCreds = cuzkUsername.trim() || cuzkApiKey.trim();
  const hasTemplates = (
    (templateFile && templateFile.size > 0)
    || (saleFlatTemplateFile && saleFlatTemplateFile.size > 0)
    || (reservationTemplateFile && reservationTemplateFile.size > 0)
    || (gdprTemplateFile && gdprTemplateFile.size > 0)
  );
  const canSubmit = isUpdate ? (hasCreds || hasTemplates) : (cuzkUsername.trim() && cuzkApiKey.trim() && cuzkPassword);

  const title = isUpdate ? t('auth.profile') : t('auth.setupRequired');
  const subtitle = isUpdate ? t('auth.profileSubtitle') : t('auth.setupSubtitle');
  const handleTemplatePick = (file, alreadyUploaded, setter) => {
    if (!file) {
      setter(null);
      return false;
    }
    if (isUpdate && alreadyUploaded) {
      const ok = window.confirm(t('auth.templateOverwriteWarning'));
      if (!ok) return false;
    }
    setter(file);
    return true;
  };
  const renderTemplateStatus = (uploaded) => (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${uploaded ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
      {uploaded ? t('auth.templateAlreadyUploaded') : t('auth.templateNotUploaded')}
    </span>
  );

  return (
    <div className="app-shell-bg min-h-[100dvh] flex flex-col items-center justify-center px-3 sm:px-5 py-8 sm:py-10 w-full max-w-full min-w-0">
      <div className="w-full max-w-md rounded-2xl border border-emerald-200/80 bg-white/95 shadow-card backdrop-blur-sm p-6 sm:p-10 min-w-0">
        <h1 className="text-2xl sm:text-3xl font-sans font-extrabold text-brand-green mb-2 tracking-[-0.03em] leading-tight">
          {title}
        </h1>
        <p className="text-slate-600 text-base sm:text-lg font-medium leading-relaxed mb-8">{subtitle}</p>
        {loadError && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-base font-medium text-amber-900 leading-relaxed">
            {loadError}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          <section className="rounded-2xl border border-emerald-100/90 bg-emerald-50/20 p-5 sm:p-6 space-y-4">
            <div>
              <h2 className="text-lg font-sans font-bold text-brand-green tracking-tight">{t('auth.credentialsSectionTitle')}</h2>
              <p className="text-sm text-slate-600 mt-1 font-medium leading-relaxed">{t('auth.credentialsSectionHint')}</p>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1.5">
                <PenIcon className="w-3.5 h-3.5 text-slate-400" />
                ČÚZK username
              </label>
              <input
                type="text"
                value={cuzkUsername}
                onChange={(e) => setCuzkUsername(e.target.value)}
                className="w-full px-4 py-3.5 text-base border border-emerald-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/40 bg-white/90"
                required={!isUpdate}
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1.5">
                <PenIcon className="w-3.5 h-3.5 text-slate-400" />
                ČÚZK password
              </label>
              <input
                type="password"
                value={cuzkPassword}
                onChange={(e) => setCuzkPassword(e.target.value)}
                className="w-full px-4 py-3.5 text-base border border-emerald-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/40 bg-white/90"
                required={!isUpdate}
                placeholder={isUpdate ? t('auth.passwordPlaceholder') : undefined}
              />
              {isUpdate && (
                <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{t('auth.passwordUpdateHint')}</p>
              )}
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1.5">
                <PenIcon className="w-3.5 h-3.5 text-slate-400" />
                ČÚZK API key
              </label>
              <input
                type="text"
                value={cuzkApiKey}
                onChange={(e) => setCuzkApiKey(e.target.value)}
                className="w-full px-4 py-3.5 text-base border border-emerald-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/40 bg-white/90"
                required={!isUpdate}
              />
            </div>
          </section>

          <section className="rounded-2xl border border-amber-100/90 bg-amber-50/15 p-5 sm:p-6 space-y-5">
            <div>
              <h2 className="text-lg font-sans font-bold text-brand-green tracking-tight">{t('auth.templatesSectionTitle')}</h2>
              <p className="text-sm text-slate-600 mt-1 font-medium leading-relaxed">{t('auth.templatesSectionHint')}</p>
            </div>

            <div className="rounded-xl border border-slate-200/70 bg-white/50 p-4 space-y-3">
              <p className="text-sm font-sans font-bold text-emerald-800 tracking-tight">{t('auth.templatesGroupRent')}</p>
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1.5">
                  <PenIcon className="w-3.5 h-3.5 text-slate-400" />
                  {t('auth.rentOrderTemplateLabel')}
                </label>
                {isUpdate && (
                  <div className="mb-2">{renderTemplateStatus(existingTemplates.templateUploaded)}</div>
                )}
                <input
                  type="file"
                  accept=".docx"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    const keep = handleTemplatePick(file, existingTemplates.templateUploaded, setTemplateFile);
                    if (!keep) e.target.value = '';
                  }}
                  className="w-full text-base text-slate-700 file:mr-3 file:py-3 file:px-4 file:rounded-xl file:border-0 file:bg-emerald-50 file:text-brand-green file:font-semibold"
                />
                {templateFile && (
                  <p className="text-sm text-amber-700 mt-1.5 leading-relaxed">
                    {t('auth.templateReplacePending')}
                  </p>
                )}
                <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{t('auth.rentOrderTemplateHint')}</p>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200/70 bg-white/50 p-4 space-y-3">
              <p className="text-sm font-sans font-bold text-slate-800 tracking-tight">{t('auth.templatesGroupOther')}</p>
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1.5">
                  <PenIcon className="w-3.5 h-3.5 text-slate-400" />
                  {t('auth.gdprTemplateLabel')}
                </label>
                {isUpdate && (
                  <div className="mb-2">{renderTemplateStatus(existingTemplates.gdprTemplateUploaded)}</div>
                )}
                <input
                  type="file"
                  accept=".docx"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    const keep = handleTemplatePick(file, existingTemplates.gdprTemplateUploaded, setGdprTemplateFile);
                    if (!keep) e.target.value = '';
                  }}
                  className="w-full text-base text-slate-700 file:mr-3 file:py-3 file:px-4 file:rounded-xl file:border-0 file:bg-emerald-50 file:text-brand-green file:font-semibold"
                />
                {gdprTemplateFile && (
                  <p className="text-sm text-amber-700 mt-1.5 leading-relaxed">
                    {t('auth.templateReplacePending')}
                  </p>
                )}
                <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{t('auth.gdprTemplateHint')}</p>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200/70 bg-white/50 p-4 space-y-3">
              <p className="text-sm font-sans font-bold text-indigo-800 tracking-tight">{t('auth.templatesGroupSaleAndReservation')}</p>
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1.5">
                  <PenIcon className="w-3.5 h-3.5 text-slate-400" />
                  {t('auth.saleFlatOrderTemplateLabel')}
                </label>
                {isUpdate && (
                  <div className="mb-2">{renderTemplateStatus(existingTemplates.saleFlatTemplateUploaded)}</div>
                )}
                <input
                  type="file"
                  accept=".docx"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    const keep = handleTemplatePick(file, existingTemplates.saleFlatTemplateUploaded, setSaleFlatTemplateFile);
                    if (!keep) e.target.value = '';
                  }}
                  className="w-full text-base text-slate-700 file:mr-3 file:py-3 file:px-4 file:rounded-xl file:border-0 file:bg-emerald-50 file:text-brand-green file:font-semibold"
                />
                {saleFlatTemplateFile && (
                  <p className="text-sm text-amber-700 mt-1.5 leading-relaxed">
                    {t('auth.templateReplacePending')}
                  </p>
                )}
                <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{t('auth.saleFlatOrderTemplateHint')}</p>
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1.5">
                  <PenIcon className="w-3.5 h-3.5 text-slate-400" />
                  {t('auth.reservationTemplateLabel')}
                </label>
                {isUpdate && (
                  <div className="mb-2">{renderTemplateStatus(existingTemplates.reservationTemplateUploaded)}</div>
                )}
                <input
                  type="file"
                  accept=".docx"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    const keep = handleTemplatePick(file, existingTemplates.reservationTemplateUploaded, setReservationTemplateFile);
                    if (!keep) e.target.value = '';
                  }}
                  className="w-full text-base text-slate-700 file:mr-3 file:py-3 file:px-4 file:rounded-xl file:border-0 file:bg-emerald-50 file:text-brand-green file:font-semibold"
                />
                {reservationTemplateFile && (
                  <p className="text-sm text-amber-700 mt-1.5 leading-relaxed">
                    {t('auth.templateReplacePending')}
                  </p>
                )}
                <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{t('auth.reservationTemplateHint')}</p>
              </div>
            </div>

            {isUpdate && <p className="text-sm text-slate-500 font-medium leading-relaxed">{t('auth.templateUpdateHint')}</p>}
          </section>
          {error && (
            <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-base font-medium text-red-800 leading-relaxed">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading || !canSubmit}
            className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-brand-emerald to-emerald-700 text-white font-bold text-base shadow-soft hover:from-emerald-700 hover:to-brand-emerald focus:outline-none focus:ring-2 focus:ring-amber-400/70 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t('auth.saving') : t('auth.saveAndContinue')}
          </button>
          {isUpdate && onDone && (
            <button
              type="button"
              onClick={onDone}
              className="w-full py-3.5 px-4 rounded-xl border-2 border-emerald-200/80 text-brand-green font-semibold text-base hover:bg-emerald-50 mt-2"
            >
              {t('auth.backToApp')}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const base64 = result.indexOf(',') >= 0 ? result.split(',')[1] : result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default SetupPage;
